// Subset of the Audiobookshelf API shapes that Plume reads.
// Everything is optional-tolerant because ABS responses vary by version and
// by whether the `minified` / `expanded` / `include` query flags were used.

export interface AbsUser {
  id: string;
  username: string;
  type?: string;
  /** Legacy, non-expiring token (deprecated in ABS 2.26+, still returned). */
  token?: string;
  /** JWT access token (ABS 2.26+ when `x-return-tokens: true` is sent). */
  accessToken?: string;
  refreshToken?: string;
  mediaProgress?: MediaProgress[];
  bookmarks?: AudioBookmark[];
}

export interface LoginResponse {
  user: AbsUser;
  userDefaultLibraryId?: string;
  serverSettings?: { version?: string };
}

/** `book` and `podcast` are what ABS ships; older or forked servers report other values. */
export type LibraryMediaType = 'book' | 'podcast' | (string & {});

export interface Library {
  id: string;
  name: string;
  /** Some servers send `title` instead of `name`; `getLibraries` normalises it into `name`. */
  title?: string;
  mediaType: LibraryMediaType;
  icon?: string;
  displayOrder?: number;
}

export interface Chapter {
  id: number;
  start: number;
  end: number;
  title: string;
}

/** Technical fields ABS reports per audio file (probe data). */
export interface AudioFileMetadata {
  filename: string;
  ext?: string;
  size?: number;
  path?: string;
  /** Probe data some servers nest under `metadata`. */
  bitRate?: number;
  bitrate?: number;
  codec?: string;
  format?: string;
}

export interface AudioFile {
  index: number;
  ino: string;
  duration: number;
  mimeType?: string;
  exclude?: boolean;
  invalid?: boolean;
  /** Bits per second. */
  bitRate?: number;
  codec?: string;
  /** Container format name from ffprobe, e.g. `mp3`, `mov,mp4,m4a,3gp,3g2,mj2`. */
  format?: string;
  channels?: number;
  channelLayout?: string;
  language?: string | null;
  metadata: AudioFileMetadata;
}

export interface AudioTrack {
  index: number;
  startOffset: number;
  duration: number;
  title?: string;
  contentUrl: string;
  mimeType?: string;
  metadata?: { filename?: string; size?: number; ext?: string };
}

export interface SeriesRef {
  id: string;
  name: string;
  sequence?: string | null;
}

export interface AuthorRef {
  id: string;
  name: string;
}

export interface BookMetadata {
  title: string;
  subtitle?: string | null;
  authorName?: string;
  authorNameLF?: string;
  authors?: AuthorRef[];
  narratorName?: string;
  narrators?: string[];
  /** Minified items carry `"Name #2"` here. */
  seriesName?: string;
  series?: SeriesRef[] | SeriesRef;
  genres?: string[];
  publishedYear?: string | null;
  publishedDate?: string | null;
  publisher?: string | null;
  description?: string | null;
  language?: string | null;
  isbn?: string | null;
  asin?: string | null;
  explicit?: boolean;
  abridged?: boolean;
}

export interface BookMedia {
  id?: string;
  metadata: BookMetadata;
  coverPath?: string | null;
  tags?: string[];
  duration?: number;
  size?: number;
  numTracks?: number;
  numAudioFiles?: number;
  numChapters?: number;
  chapters?: Chapter[];
  audioFiles?: AudioFile[];
  tracks?: AudioTrack[];
  ebookFile?: unknown;
  ebookFormat?: string | null;
}

export interface MediaProgress {
  id?: string;
  libraryItemId: string;
  episodeId?: string | null;
  /** Alternate id keys older servers and Flutter's parser accept. */
  itemId?: string;
  mediaItemId?: string;
  mediaItemType?: string;
  duration: number;
  progress: number;
  currentTime: number;
  isFinished: boolean;
  hideFromContinueListening?: boolean;
  /** Epoch ms. */
  lastUpdate: number;
  startedAt?: number;
  /** Epoch ms (some servers send seconds; see `parseEpochMs`). */
  finishedAt?: number | null;
  ebookLocation?: string | null;
  ebookProgress?: number | null;
}

export interface LibraryItem {
  id: string;
  libraryId: string;
  mediaType: 'book' | 'podcast';
  media: BookMedia;
  ino?: string;
  addedAt?: number;
  updatedAt?: number;
  isMissing?: boolean;
  isInvalid?: boolean;
  numFiles?: number;
  size?: number;
  userMediaProgress?: MediaProgress;
  /** Alternate progress keys some endpoints use (`include=progress`). */
  mediaProgress?: MediaProgress;
  progress?: MediaProgress;
  /** Present on items returned inside a collapsed-series result. */
  collapsedSeries?: { id: string; name: string; numBooks: number; libraryItemIds?: string[] };
  /** Present on items inside a series shelf. */
  sequence?: string;
}

export interface PaginatedItems<T> {
  results: T[];
  total: number;
  limit: number;
  page: number;
}

/** `include=progress` on series endpoints. */
export interface SeriesProgress {
  libraryItemIds?: string[];
  libraryItemIdsFinished?: string[];
  isFinished?: boolean;
  /** Single-item variants some servers send. */
  libraryItemId?: string;
  itemId?: string;
}

export interface Series {
  id: string;
  name: string;
  nameIgnorePrefix?: string;
  description?: string | null;
  books?: LibraryItem[];
  /** Alternate book arrays (search results, older servers). */
  libraryItems?: Array<LibraryItem | { libraryItem?: LibraryItem | string; libraryItemId?: string; userMediaProgress?: MediaProgress }>;
  items?: LibraryItem[];
  libraryItemIds?: string[];
  numBooks?: number;
  progress?: SeriesProgress;
  addedAt?: number;
  updatedAt?: number;
  lastUpdate?: number;
  createdAt?: number;
  totalDuration?: number;
  libraryId?: string;
  /** Search results wrap the series in `{ series, books }`. */
  series?: Partial<Series>;
}

export interface Author {
  id: string;
  name: string;
  description?: string | null;
  imagePath?: string | null;
  asin?: string | null;
  lastFirst?: string;
  numBooks?: number;
  addedAt?: number;
  updatedAt?: number;
  libraryId?: string;
  libraryItems?: LibraryItem[];
  series?: Array<Series & { items?: LibraryItem[] }>;
}

export interface Collection {
  id: string;
  name: string;
  description?: string | null;
  books: LibraryItem[];
  libraryId?: string;
  userId?: string;
  addedAt?: number;
  lastUpdate?: number;
  createdAt?: number;
}

export type ShelfType = 'book' | 'series' | 'authors' | 'episode' | 'podcast';

export interface Shelf {
  id: string;
  label: string;
  /** Older servers label shelves with `title` or `category`. */
  title?: string;
  category?: string;
  labelStringKey?: string;
  type: ShelfType;
  entities: Array<LibraryItem | Series | Author>;
  total?: number;
}

export const PlayMethod = {
  DirectPlay: 0,
  DirectStream: 1,
  Transcode: 2,
  Local: 3,
} as const;

export interface PlaybackSession {
  id: string;
  libraryItemId: string;
  episodeId?: string | null;
  displayTitle?: string;
  displayAuthor?: string;
  coverPath?: string | null;
  duration: number;
  playMethod: number;
  currentTime: number;
  startTime?: number;
  chapters?: Chapter[];
  audioTracks: AudioTrack[];
  mediaMetadata?: BookMetadata;
  libraryItem?: LibraryItem;
}

export interface AudioBookmark {
  libraryItemId: string;
  title: string;
  time: number;
  createdAt: number;
}

/** `GET /api/me/listening-sessions` entry. */
export interface ListeningSession {
  id: string;
  userId?: string;
  libraryId?: string;
  libraryItemId: string;
  episodeId?: string | null;
  mediaType?: string;
  displayTitle?: string;
  displayAuthor?: string;
  mediaMetadata?: BookMetadata & { coverPath?: string };
  coverPath?: string | null;
  /** Book duration, seconds. */
  duration: number;
  playMethod?: number;
  mediaPlayer?: string;
  /** Seconds actually listened in this session. */
  timeListening: number;
  startTime?: number;
  currentTime: number;
  /** Server-local `YYYY-MM-DD`. */
  date?: string;
  dayOfWeek?: string;
  /** Epoch ms. */
  startedAt: number;
  updatedAt: number;
}

export interface ServerStatus {
  isInit?: boolean;
  serverVersion?: string;
  authMethods?: string[];
  language?: string;
}
